// Real HTTP client: same interface as the mock, every call through the public API with a cookie session.
import type { CoachBrainApi } from "./types";

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) { super(message); }
}

async function call<T>(method: string, path: string, body?: unknown, query?: Record<string, string | undefined>): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (query) for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== "") url.searchParams.set(k, v);
  const res = await fetch(url.toString(), {
    method, credentials: "include",
    headers: { "content-type": "application/json", accept: "application/json", ...(method !== "GET" ? { "idempotency-key": crypto.randomUUID() } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) throw new ApiError(res.status, (data as { error?: { message?: string } })?.error?.message ?? res.statusText, data);
  return data as T;
}
const enc = encodeURIComponent;

export function createHttpApi(): CoachBrainApi {
  return {
    me: async () => { try { return await call("GET", "/api/me"); } catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; } },
    signup: (input) => call("POST", "/api/auth/signup", input),
    login: (input) => call("POST", "/api/auth/login", input),
    logout: () => call("POST", "/api/auth/logout"),
    authConfig: () => call("GET", "/api/auth/config"),
    googlePending: (ticket) => call("GET", "/api/auth/google/pending", undefined, { ticket }),
    googleComplete: (input) => call("POST", "/api/auth/google/complete", input),
    listClients: () => call("GET", "/api/clients"),
    addClient: (input) => call("POST", "/api/clients", input),
    overview: (id) => call("GET", `/api/clients/${enc(id)}/overview`),
    today: () => call("GET", "/api/today"),
    createCapture: (id, input) => call("POST", `/api/clients/${enc(id)}/captures`, input),
    getCapture: (id) => call("GET", `/api/captures/${enc(id)}`),
    saveCapture: (id, input) => call("POST", `/api/captures/${enc(id)}/save`, input),
    plannedForClient: (id) => call("GET", `/api/clients/${enc(id)}/plan`),
    answerQuestion: (id, answer) => call("POST", `/api/questions/${enc(id)}/answer`, { answer }),
    adoptPlan: (id) => call("POST", `/api/plans/${enc(id)}/adopt`),
    editPlan: (id, edits) => call("POST", `/api/plans/${enc(id)}/edit`, edits),
    swapPlan: (id, alternative) => call("POST", `/api/plans/${enc(id)}/swap`, { alternative }),
    discardPlan: (id) => call("POST", `/api/plans/${enc(id)}/discard`),
    alternativePlan: (id) => call("GET", `/api/plans/${enc(id)}/alternative`),
    history: (id, opts) => call("GET", `/api/clients/${enc(id)}/history`, undefined, { exerciseId: opts?.exerciseId, tab: opts?.tab }),
    challenges: (id) => call("GET", `/api/clients/${enc(id)}/challenges`),
    dismissChallenge: (id, input) => call("POST", `/api/challenges/${enc(id)}/dismiss`, input ?? {}),
    challengeAddToToday: (id) => call("POST", `/api/challenges/${enc(id)}/add-to-today`),
    challengeAskClient: (id) => call("POST", `/api/challenges/${enc(id)}/ask-client`),
    dismissConsideration: (id, input) => call("POST", `/api/considerations/${enc(id)}/dismiss`, input ?? {}),
    snoozeConsideration: (id, until) => call("POST", `/api/considerations/${enc(id)}/snooze`, { until }),
    draftFromConsideration: (id) => call("POST", `/api/considerations/${enc(id)}/draft`),
    pendingReviews: (id) => call("GET", `/api/clients/${enc(id)}/reviews/pending`),
    getReview: (id) => call("GET", `/api/reviews/${enc(id)}`),
    approveReview: (id, edits) => call("POST", `/api/reviews/${enc(id)}/approve`, edits ?? {}),
    reports: (id) => call("GET", `/api/clients/${enc(id)}/reports`),
    draftReport: (id, input) => call("POST", `/api/clients/${enc(id)}/reports`, input),
    sendReport: (id) => call("POST", `/api/reports/${enc(id)}/send`),
    settings: () => call("GET", "/api/practice/settings"),
    updateSettings: (patch) => call("PATCH", "/api/practice/settings", patch),
    requestExport: () => call("POST", "/api/practice/export"),
    exercises: () => call("GET", "/api/exercises"),
    billing: () => call("GET", "/api/billing"),
    billingCheckout: (planId) => call("POST", "/api/billing/checkout", { planId }),
    billingPortal: () => call("POST", "/api/billing/portal"),
    traineeHome: () => call("GET", "/api/t/home"),
    traineeReport: (id) => call("GET", `/api/t/reports/${enc(id)}`),
    traineeSession: (id) => call("GET", `/api/t/sessions/${enc(id)}`),
    traineeLog: (input) => call("POST", "/api/t/sessions/log", input),
    traineeAnswer: (id, answer) => call("POST", `/api/t/questions/${enc(id)}/answer`, { answer }),
    traineeMessage: (text) => call("POST", "/api/t/messages", { text }),
    traineeData: () => call("GET", "/api/t/data"),
    traineeDelete: () => call("DELETE", "/api/t/account"),
    traineeRevokeConsent: (id) => call("POST", `/api/t/consents/${enc(id)}/revoke`),
    adminPractices: () => call("GET", "/api/admin/practices"),
    adminSetPracticeStatus: (id, status) => call("POST", `/api/admin/practices/${enc(id)}/status`, { status }),
    adminCompPractice: (id, days) => call("POST", `/api/admin/practices/${enc(id)}/comp`, { days }),
    adminFlags: () => call("GET", "/api/admin/flags"),
    adminSetFlag: (key, enabled, practiceId) => call("PUT", `/api/admin/flags/${enc(key)}`, { enabled, practiceId }),
    adminMethods: () => call("GET", "/api/admin/methods"),
    adminSaveMethod: (m) => call("PUT", `/api/admin/methods/${enc(m.id)}`, m),
    adminKnowledge: () => call("GET", "/api/admin/knowledge"),
    adminSaveKnowledge: (k) => call("PUT", `/api/admin/knowledge/${enc(k.id)}`, k),
    adminProviders: () => call("GET", "/api/admin/providers"),
    adminSaveProvider: (p) => call("PUT", `/api/admin/providers/${enc(p.id)}`, p),
    adminChannels: () => call("GET", "/api/admin/channels"),
    adminSetChannel: (kind, enabled) => call("PUT", `/api/admin/channels/${enc(kind)}`, { enabled }),
    adminAudit: (q) => call("GET", "/api/admin/audit", undefined, q),
    adminRequestSupportAccess: (id, hours, reason) => call("POST", `/api/admin/practices/${enc(id)}/support-access`, { hours, reason }),
  };
}
